import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type CollectionResponse, type ResourceResponse } from "@shared/api/client";
import type {
  Asiento,
  AsientoFormData,
  Balance,
  ConfigContable,
  CuadrePreview,
  Cuenta,
  CuentaFormData,
  Mayor,
  Modelo303,
  Pyg,
  SumaSaldo,
} from "../types";

const BASE = "/accounting";

export const accKeys = {
  all: ["accounting"] as const,
  accounts: (emisorId: number, filters?: Record<string, unknown>) =>
    [...accKeys.all, "accounts", emisorId, filters ?? {}] as const,
  entries: (emisorId: number, filters?: Record<string, unknown>) =>
    [...accKeys.all, "entries", emisorId, filters ?? {}] as const,
  entry: (emisorId: number, id: number) =>
    [...accKeys.all, "entry", emisorId, id] as const,
  ledger: (emisorId: number, cuenta: string, ejercicio: number) =>
    [...accKeys.all, "ledger", emisorId, cuenta, ejercicio] as const,
  balance: (emisorId: number, desde: string, hasta: string) =>
    [...accKeys.all, "balance", emisorId, desde, hasta] as const,
  pyg: (emisorId: number, desde: string, hasta: string) =>
    [...accKeys.all, "pyg", emisorId, desde, hasta] as const,
  trial: (emisorId: number, desde: string, hasta: string) =>
    [...accKeys.all, "trial", emisorId, desde, hasta] as const,
  m303: (emisorId: number, year: number, trimestre: number) =>
    [...accKeys.all, "m303", emisorId, year, trimestre] as const,
  config: (emisorId: number) => [...accKeys.all, "config", emisorId] as const,
};

// ───────────────────────── Plan de cuentas ─────────────────────────

export function useCuentas(
  emisorId: number | null,
  filters?: { grupo?: number; activa?: boolean; estado?: string; q?: string },
) {
  return useQuery({
    queryKey: accKeys.accounts(emisorId ?? 0, filters),
    queryFn: async () => {
      const { data } = await api.get<CollectionResponse<Cuenta>>(`${BASE}/accounts`, {
        params: { emisor_id: emisorId, ...filters },
      });
      return data.data;
    },
    enabled: !!emisorId,
  });
}

export function useCreateCuenta() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CuentaFormData) => {
      const { data } = await api.post<ResourceResponse<Cuenta>>(`${BASE}/accounts`, payload);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: accKeys.all }),
  });
}

export function useUpdateCuenta() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: Partial<CuentaFormData> }) => {
      const { data } = await api.patch<ResourceResponse<Cuenta>>(`${BASE}/accounts/${id}`, payload);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: accKeys.all }),
  });
}

export function useDeleteCuenta() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => api.delete(`${BASE}/accounts/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: accKeys.all }),
  });
}

// ───────────────────────── Asientos / Diario ─────────────────────────

export function useAsientos(
  emisorId: number | null,
  filters?: { ejercicio?: number; desde?: string; hasta?: string; q?: string },
) {
  return useQuery({
    queryKey: accKeys.entries(emisorId ?? 0, filters),
    queryFn: async () => {
      const { data } = await api.get<CollectionResponse<Asiento>>(`${BASE}/entries`, {
        params: { emisor_id: emisorId, ...filters },
      });
      return data.data;
    },
    enabled: !!emisorId,
  });
}

export function useAsiento(emisorId: number | null, id: number | null) {
  return useQuery({
    queryKey: accKeys.entry(emisorId ?? 0, id ?? 0),
    queryFn: async () => {
      const { data } = await api.get<ResourceResponse<Asiento>>(`${BASE}/entries/${id}`, {
        params: { emisor_id: emisorId },
      });
      return data.data;
    },
    enabled: !!emisorId && !!id,
  });
}

export function useCreateAsiento() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: AsientoFormData) => {
      const { data } = await api.post<ResourceResponse<Asiento>>(`${BASE}/entries`, payload);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: accKeys.all }),
  });
}

export function useAnularAsiento() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, emisorId }: { id: number; emisorId: number }) => {
      const { data } = await api.post<{ ok: boolean; contraasiento: Asiento }>(
        `${BASE}/entries/${id}/void`,
        null,
        { params: { emisor_id: emisorId } },
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: accKeys.all }),
  });
}

export async function previewCuadre(
  apuntes: Array<{ cuenta_codigo: string; debe: number; haber: number }>,
): Promise<CuadrePreview> {
  const { data } = await api.post<ResourceResponse<CuadrePreview>>(
    `${BASE}/entries/preview-balance`,
    { apuntes },
  );
  return data.data;
}

// ───────────────────────── Mayor ─────────────────────────

export function useMayor(emisorId: number | null, cuenta: string | null, ejercicio: number) {
  return useQuery({
    queryKey: accKeys.ledger(emisorId ?? 0, cuenta ?? "", ejercicio),
    queryFn: async () => {
      const { data } = await api.get<ResourceResponse<Mayor>>(`${BASE}/ledger`, {
        params: { emisor_id: emisorId, cuenta, ejercicio },
      });
      return data.data;
    },
    enabled: !!emisorId && !!cuenta,
  });
}

// ───────────────────────── Informes ─────────────────────────

export function useBalance(emisorId: number | null, desde: string, hasta: string) {
  return useQuery({
    queryKey: accKeys.balance(emisorId ?? 0, desde, hasta),
    queryFn: async () => {
      const { data } = await api.get<ResourceResponse<Balance>>(`${BASE}/reports/balance-sheet`, {
        params: { emisor_id: emisorId, desde, hasta },
      });
      return data.data;
    },
    enabled: !!emisorId,
  });
}

export function usePyg(emisorId: number | null, desde: string, hasta: string) {
  return useQuery({
    queryKey: accKeys.pyg(emisorId ?? 0, desde, hasta),
    queryFn: async () => {
      const { data } = await api.get<ResourceResponse<Pyg>>(`${BASE}/reports/income-statement`, {
        params: { emisor_id: emisorId, desde, hasta },
      });
      return data.data;
    },
    enabled: !!emisorId,
  });
}

export function useTrialBalance(emisorId: number | null, desde: string, hasta: string) {
  return useQuery({
    queryKey: accKeys.trial(emisorId ?? 0, desde, hasta),
    queryFn: async () => {
      const { data } = await api.get<CollectionResponse<SumaSaldo>>(
        `${BASE}/reports/trial-balance`,
        { params: { emisor_id: emisorId, desde, hasta } },
      );
      return data.data;
    },
    enabled: !!emisorId,
  });
}

// ───────────────────────── Modelo 303 ─────────────────────────

export function useModelo303(
  emisorId: number | null,
  year: number,
  trimestre: number,
) {
  return useQuery({
    queryKey: accKeys.m303(emisorId ?? 0, year, trimestre),
    queryFn: async () => {
      const { data } = await api.get<ResourceResponse<Modelo303>>(`${BASE}/vat/modelo-303`, {
        params: { emisor_id: emisorId, year, trimestre },
      });
      return data.data;
    },
    enabled: !!emisorId,
  });
}

// ───────────────────────── Config contable ─────────────────────────

export function useConfigContable(emisorId: number | null) {
  return useQuery({
    queryKey: accKeys.config(emisorId ?? 0),
    queryFn: async () => {
      const { data } = await api.get<ResourceResponse<ConfigContable>>(`${BASE}/config`, {
        params: { emisor_id: emisorId },
      });
      return data.data;
    },
    enabled: !!emisorId,
  });
}

export function useUpdateConfigContable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      emisorId,
      payload,
    }: {
      emisorId: number;
      payload: Partial<ConfigContable>;
    }) => {
      const { data } = await api.patch<ResourceResponse<ConfigContable>>(
        `${BASE}/config`,
        payload,
        { params: { emisor_id: emisorId } },
      );
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: accKeys.all }),
  });
}
